'use client'

// libraries
import React, { useRef, useCallback, useEffect } from 'react'
import { gsap } from 'gsap'
import { useGSAP } from '@gsap/react'

interface UseResultsAnimationOptions {
    duration?: number
    stagger?: number
    ease?: string
}

interface UseResultsAnimationReturn {
    containerRef: React.RefObject<HTMLDivElement>
    animateFilterChange: (onFilterComplete: () => void) => void
}

export const useResultsAnimation = (
    dependencies: React.DependencyList = [],
    options: UseResultsAnimationOptions = {}
): UseResultsAnimationReturn => {
    const containerRef = useRef<HTMLDivElement>(null)
    const isInitialLoad = useRef(true)
    const previousItemCount = useRef(0)
    const isAnimatingFilter = useRef(false)
    const hasTriggeredInitialAnimation = useRef(false)
    
    const {
        duration = 0.4,
        stagger = 0.05,
        ease = 'power2.out'
    } = options

    // Reusable animation functions
    const fadeIn = useCallback((elements: NodeListOf<Element> | Element[], animationDuration = duration, delay = 0.1) => {
        gsap.fromTo(elements, 
            {
                opacity: 0,
                y: 10,
                visibility: 'visible'
            },
            {
                opacity: 1,
                y: 0,
                duration: animationDuration,
                stagger,
                ease,
                delay,
                overwrite: true
            }
        )
    }, [duration, stagger, ease])

    const fadeOut = useCallback((elements: NodeListOf<Element> | Element[], animationDuration = duration * 0.4, onComplete?: () => void) => {
        gsap.to(elements, {
            opacity: 0,
            y: -10,
            duration: animationDuration,
            stagger: stagger * 0.35,
            ease: 'power2.in',
            onComplete
        })
    }, [duration, stagger])

    const getItems = useCallback(() => {
        return containerRef.current?.querySelectorAll('.results__list__item')
    }, [])

    const animateFilterChange = useCallback((onFilterComplete: () => void) => {
        const items = getItems()
        
        if (!items || items.length === 0) {
            onFilterComplete()
            return
        }

        isAnimatingFilter.current = true
        fadeOut(items, duration * 0.4, onFilterComplete)
    }, [duration, fadeOut, getItems])

    useGSAP(() => {
        const items = getItems()
        if (!items || items.length === 0) return

        const currentItemCount = items.length
        const isLoadMore = currentItemCount > previousItemCount.current && !isInitialLoad.current

        gsap.killTweensOf(items)

        if (isAnimatingFilter.current) {
            // Filter animation - fade in new results
            fadeIn(items, duration * 0.6, 0.1)
            isAnimatingFilter.current = false
            previousItemCount.current = currentItemCount
            return
        }

        if (isInitialLoad.current || !hasTriggeredInitialAnimation.current) {
            // Initial load - fade in all items
            fadeIn(items)
            isInitialLoad.current = false
            hasTriggeredInitialAnimation.current = true
        } else if (isLoadMore) {
            // Load more - only animate new items
            const newItems = Array.from(items).slice(previousItemCount.current)
            fadeIn(newItems)
        }

        previousItemCount.current = currentItemCount
    }, { 
        scope: containerRef, 
        dependencies: [...dependencies] 
    })

    // Fallback mechanism using Intersection Observer for production builds
    useEffect(() => {
        if (!containerRef.current) return

        const observer = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    if (entry.isIntersecting && !hasTriggeredInitialAnimation.current) {
                        const items = getItems()
                        if (items && items.length > 0) {
                            // Small delay to ensure DOM is fully ready
                            setTimeout(() => {
                                fadeIn(items)
                                hasTriggeredInitialAnimation.current = true
                                observer.unobserve(entry.target)
                            }, 50)
                        }
                    }
                })
            },
            {
                threshold: 0.1,
                rootMargin: '50px'
            }
        )

        observer.observe(containerRef.current)

        return () => {
            observer.disconnect()
        }
    }, [fadeIn, getItems])

    return { containerRef, animateFilterChange }
}